import { useEffect, useMemo } from 'react';
import { useCoreLoopConfigurationStore } from '@core/coreLoopConfiguration/useCoreLoopConfigurationStore';
import usePortalAccess from '@/access/usePortalAccess';
import { useTenantId } from '@/contexts/TenantContext';
import type { FeatureViewInput } from '@/view';

const useFeatureView = <TView>(
  getView: (input: FeatureViewInput) => TView,
): TView => {
  const access = usePortalAccess();
  const tenantId = useTenantId();
  const { coreLoopConfigurationItems, ensureCoreLoopConfiguration } =
    useCoreLoopConfigurationStore();

  useEffect(() => {
    if (tenantId) {
      void ensureCoreLoopConfiguration(tenantId);
    }
  }, [tenantId, ensureCoreLoopConfiguration]);

  const clientsCanSeePayRates =
    !!coreLoopConfigurationItems?.clientsCanSeePayRates;
  const tenantRequiresPostingQuestionnaire = Boolean(
    coreLoopConfigurationItems?.tenantRequiresPostingQuestionnaire,
  );
  const allowClientsToSeeProtectedAssets = Boolean(
    coreLoopConfigurationItems?.allowClientsToSeeProtectedAssets,
  );

  return useMemo(
    () =>
      getView({
        access,
        clientsCanSeePayRates,
        tenantRequiresPostingQuestionnaire,
        allowClientsToSeeProtectedAssets,
      }),
    [
      getView,
      access,
      clientsCanSeePayRates,
      tenantRequiresPostingQuestionnaire,
      allowClientsToSeeProtectedAssets,
    ],
  );
};

export default useFeatureView;
